import React from 'react'
import {Box, Text, List, ListItem, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import {UnorderedList} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useLocation} from 'react-router-dom'
import {useCountrySelector} from '../../../hooks/use-country-selector'
import {buildCrossSiteLink} from '../../../utils/utils'
import {useBrand} from '../../../contexts/brand-context'
import {useHeaderContext} from '../../../contexts/header-context'

export const GiftCallout = () => {
    const {locale} = useMultiSite()
    const location = useLocation()
    const {brandId, isGarage} = useBrand()
    const {countrySelectorData} = useCountrySelector()
    const {product, category} = useHeaderContext()

    const isCA = locale?.id?.includes('CA') || locale?.alias?.toLowerCase().includes('ca')
    const isUS = locale?.id?.includes('US') || locale?.alias?.toLowerCase().includes('us')

    const crossSiteLink = (targetRegion) =>
        buildCrossSiteLink({
            targetRegion,
            product,
            category,
            countrySelectorData,
            brandId,
            pathname: location.pathname
        })


    if (isCA) {
        return (
            <Box my={4} className="notes">
                <Text textStyle={'labelXs'} mb={2}>
                    <FormattedMessage id="gift_callout.note.label" defaultMessage="Notes" />
                </Text>
                <Text textStyle="bodyXsRegular" mt={2}>
                    {isGarage ? (
                        <FormattedMessage
                            id="gift_callout.note.ca_currency.garage"
                            defaultMessage="This gift card will be in <bold>CAD</bold>. To purchase in <bold>USD</bold> click {link}."
                            values={{
                                link: (
                                    <Link href={crossSiteLink('US')} textDecoration="underline">
                                        <FormattedMessage
                                            id="gift_callout.note.currency.link"
                                            defaultMessage="here"
                                        />
                                    </Link>
                                ),
                                bold: (chunks) => (
                                    <Text as="span" fontWeight="bold">
                                        {chunks}
                                    </Text>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="gift_callout.note.ca_currency.dynamite"
                            defaultMessage="This gift card will be in <bold>CAD</bold>. To purchase in <bold>USD</bold> click {link}."
                            values={{
                                link: (
                                    <Link href={crossSiteLink('US')} textDecoration="underline">
                                        <FormattedMessage
                                            id="gift_callout.note.currency.link"
                                            defaultMessage="here"
                                        />
                                    </Link>
                                ),
                                bold: (chunks) => (
                                    <Text as="span" fontWeight="bold">
                                        {chunks}
                                    </Text>
                                )
                            }}
                        />
                    )}
                </Text>
                <Text textStyle="bodyXsRegular" mt={1}>
                    <FormattedMessage
                        id="gift_callout.note.klarna_ineligible"
                        defaultMessage="Gift cards are not eligible for checkout with Klarna."
                    />
                </Text>
            </Box>
        )
    }

    if (isUS) {
        return (
            <Box my={4} className="notes">
                <Text textStyle={'labelXs'} mb={2}>
                    <FormattedMessage id="gift_callout.note.label" defaultMessage="Notes" />
                </Text>
                <Text textStyle="bodyXsRegular" mt={2}>
                    {isGarage ? (
                        <FormattedMessage
                            id="gift_callout.note.us_currency.garage"
                            defaultMessage="This gift card will be in <bold>USD</bold>. To purchase in <bold>CAD</bold> click {link}."
                            values={{
                                link: (
                                    <Link href={crossSiteLink('CA')} textDecoration="underline">
                                        <FormattedMessage
                                            id="gift_callout.note.currency.link"
                                            defaultMessage="here"
                                        />
                                    </Link>
                                ),
                                bold: (chunks) => (
                                    <Text as="span" fontWeight="bold">
                                        {chunks}
                                    </Text>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="gift_callout.note.us_currency.dynamite"
                            defaultMessage="This gift card will be in <bold>USD</bold>. To purchase in <bold>CAD</bold> click {link}."
                            values={{
                                link: (
                                    <Link href={crossSiteLink('CA')} textDecoration="underline">
                                        <FormattedMessage
                                            id="gift_callout.note.currency.link"
                                            defaultMessage="here"
                                        />
                                    </Link>
                                ),
                                bold: (chunks) => (
                                    <Text as="span" fontWeight="bold">
                                        {chunks}
                                    </Text>
                                )
                            }}
                        />
                    )}
                </Text>
                <Text textStyle="bodyXsRegular" mt={1}>
                    <FormattedMessage
                        id="gift_callout.note.klarna_ineligible"
                        defaultMessage="Gift cards are not eligible for checkout with Klarna."
                    />
                </Text>
            </Box>
        )
    }

    return (
        <Box bg="alternativeWarning.100" color="warningText" p={4} fontSize="sm">
            <Text fontWeight="bold">
                <FormattedMessage id="gift_callout.note.label" defaultMessage="Notes" />
            </Text>
            <UnorderedList pl={2}>
                <ListItem>
                    <FormattedMessage
                        id="gift_callout.note.uk_only"
                        defaultMessage="This voucher can only be used in the <bold>United Kingdom</bold> in <bold>£ GBP</bold>."
                        values={{
                            bold: (chunks) => (
                                <Text as="span" fontWeight="bold">
                                    {chunks}
                                </Text>
                            )
                        }}
                    />
                </ListItem>
                <ListItem>
                    <FormattedMessage
                        id="gift_callout.note.klarna_ineligible"
                        defaultMessage="Gift cards are not eligible for checkout with Klarna."
                    />
                </ListItem>
                <ListItem>
                    <FormattedMessage
                        id="gift_callout.note.final_sale"
                        defaultMessage="This item is <bold>final sale</bold> and not eligible for points."
                        values={{
                            bold: (chunks) => (
                                <Text as="span" fontWeight="bold">
                                    {chunks}
                                </Text>
                            )
                        }}
                    />
                </ListItem>
                <ListItem>
                    {isGarage ? (
                        <FormattedMessage
                            id="gift_callout.note.cancellation_waiver.garage"
                            defaultMessage="By purchasing an e-voucher, you are consenting to immediate delivery and acknowledge that you are waiving your right to cancellation. Your statutory rights are not affected."
                        />
                    ) : (
                        <FormattedMessage
                            id="gift_callout.note.cancellation_waiver.dynamite"
                            defaultMessage="By purchasing an e-voucher, you are consenting to immediate delivery and acknowledge that you are waiving your right to cancellation. Your statutory rights are not affected."
                        />
                    )}
                </ListItem>
            </UnorderedList>
        </Box>
    )
}
